import React, { useState, useEffect, useRef, useMemo } from 'react';

import { useRouter } from 'next/navigation';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Collapse,
  useTheme as useMuiTheme,
  keyframes,
} from '@mui/material';

import { useOmniBox } from '@/context/OmniBoxContext';
import { useOmniBoxWidth } from '@/hooks/useOmniBoxWidth';
import { ThreadTopicEntry, DisplayConceptRef } from '@/lib/api';
import { useTheme } from '@/theme/ThemeContext';
import { COLOR_MAPPING, resolveColors } from '@/theme/colorPalettes';
import { createElevationStyles } from '@/theme/elevationStyles';

import { ProductMiniCard } from '../ProductMiniCard';

interface MobileContextPillsProps {
  topics: ThreadTopicEntry[];
  currentTopicIndex: number;
  onTopicSelect: (index: number) => void;
  starredProducts: DisplayConceptRef[];
  visible: boolean;
}

// Animation keyframes for rolling effect
const rollOutUp = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-100%);
    opacity: 0;
  }
`;

const rollInUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const rollOutDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const rollInDown = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

export const MobileContextPills: React.FC<MobileContextPillsProps> = ({
  topics,
  currentTopicIndex,
  onTopicSelect,
  starredProducts,
  visible,
}) => {
  const muiTheme = useMuiTheme();
  const { mode } = useTheme(); // This gives us the resolved mode ('light' or 'dark')
  const router = useRouter();
  const [showToc, setShowToc] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const elevationStyles = createElevationStyles(muiTheme, 1);
  const { width, maxWidth, minWidth } = useOmniBoxWidth();
  const { isExpanded } = useOmniBox();

  // Check for reduced motion preference
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  // Resolve colors for floating widgets
  const colors = useMemo(() => {
    const resolved = resolveColors(COLOR_MAPPING, mode);
    return {
      bg: resolved.floatingWidgetBg,
      fg: resolved.floatingWidgetFg,
    };
  }, [mode]);

  // Animation state
  const [displayIndex, setDisplayIndex] = useState(currentTopicIndex);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState<'up' | 'down'>('up');
  const prevIndexRef = useRef(currentTopicIndex);

  // Handle animation when topic index changes
  useEffect(() => {
    if (currentTopicIndex !== prevIndexRef.current && currentTopicIndex !== displayIndex) {
      if (prefersReducedMotion) {
        // Skip animation if user prefers reduced motion
        setDisplayIndex(currentTopicIndex);
        prevIndexRef.current = currentTopicIndex;
        setIsAnimating(false);
      } else {
        // Determine animation direction based on index change
        const direction = currentTopicIndex > prevIndexRef.current ? 'up' : 'down';
        setAnimationDirection(direction);
        setIsAnimating(true);

        // After animation starts, update the display index
        setTimeout(() => {
          setDisplayIndex(currentTopicIndex);
          prevIndexRef.current = currentTopicIndex;
        }, 150); // Half of animation duration for smooth transition

        // Reset animation state after completion
        setTimeout(() => {
          setIsAnimating(false);
        }, 350); // Slightly longer than animation duration to prevent flash
      }
    }
  }, [currentTopicIndex, displayIndex, prefersReducedMotion]);

  const handleTocClick = () => {
    setShowToc(!showToc);
    setShowProducts(false); // Close products if open
  };

  const handleTopicSelect = (index: number) => {
    onTopicSelect(index);
    setShowToc(false);
  };

  const toggleProducts = () => {
    setShowProducts(!showProducts);
    setShowToc(false); // Close ToC if open
  };

  if (!visible || isExpanded) {
    return null;
  }

  const currentTopic = topics[displayIndex] || topics[currentTopicIndex];
  const nextTopic = topics[currentTopicIndex];

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 72, // Above OmniBox (48px height + 16px margin + 8px spacing)
        left: '50%',
        transform: 'translateX(-50%)',
        width: width,
        maxWidth: maxWidth,
        minWidth: minWidth,
        zIndex: muiTheme.zIndex.appBar - 1,
      }}
    >
      {/* Main card container - expands when content is shown */}
      <Box
        sx={{
          minHeight: 48,
          borderRadius: showToc || showProducts ? 3 : 24, // Full oval when collapsed, rounded corners when expanded
          backgroundColor: colors.bg,
          color: colors.fg,
          ...createElevationStyles(muiTheme, 3), // Slightly less elevation than OmniBox
          // Add shadow for light mode
          ...(mode === 'light' && {
            boxShadow: muiTheme.shadows[6], // Less shadow than OmniBox
          }),
          // Add subtle border
          border: `1px solid ${muiTheme.palette.divider}`,
          transition: 'all 0.3s ease',
          overflow: 'hidden',
        }}
      >
        {/* Top bar section */}
        <Box
          sx={{
            height: 48,
            display: 'flex',
            alignItems: 'center',
            px: 1.5, // Reduced from 2.5
            gap: 1, // Reduced from 2
          }}
        >
          {/* ToC Selector Section */}
          <Box
            onClick={handleTocClick}
            role="button"
            tabIndex={0}
            aria-label={`Table of contents. Current section: ${currentTopic?.summary || 'Select section'}`}
            aria-expanded={showToc}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleTocClick();
              }
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              borderRadius: 2,
              px: 1, // Reduced from 1.5
              py: 0.5, // Reduced from 0.75
              '&:hover': {
                backgroundColor: muiTheme.palette.action.hover,
              },
            }}
          >
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                position: 'relative',
                height: 24,
                mr: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {isAnimating && !prefersReducedMotion && (
                <Typography
                  variant="body2"
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    animation: `${animationDirection === 'up' ? rollOutUp : rollOutDown} 0.3s ease-out`,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: colors.fg,
                    willChange: 'transform',
                    backfaceVisibility: 'hidden',
                    '-webkit-font-smoothing': 'antialiased',
                    '-moz-osx-font-smoothing': 'grayscale',
                  }}
                >
                  {topics[prevIndexRef.current]?.summary || 'Select section'}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  position: 'absolute',
                  width: '100%',
                  animation:
                    isAnimating && !prefersReducedMotion
                      ? `${animationDirection === 'up' ? rollInUp : rollInDown} 0.3s ease-out`
                      : 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: colors.fg,
                  willChange: 'transform',
                  backfaceVisibility: 'hidden',
                  '-webkit-font-smoothing': 'antialiased',
                  '-moz-osx-font-smoothing': 'grayscale',
                }}
              >
                {currentTopic?.summary || 'Select section'}
              </Typography>
            </Box>
            {showToc ? (
              <ExpandLessIcon fontSize="small" sx={{ color: colors.fg }} />
            ) : (
              <ExpandMoreIcon fontSize="small" sx={{ color: colors.fg }} />
            )}
          </Box>

          {/* Removed divider for more space */}

          {/* Starred Products Section */}
          {starredProducts.length > 0 && (
            <Box
              onClick={toggleProducts}
              role="button"
              tabIndex={0}
              aria-label={`Starred products. ${starredProducts.length} products`}
              aria-expanded={showProducts}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleProducts();
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                borderRadius: 2,
                px: 1, // Reduced from 1.5
                py: 0.5, // Reduced from 0.75
                '&:hover': {
                  backgroundColor: muiTheme.palette.action.hover,
                },
              }}
            >
              {/* Product thumbnails */}
              <Box sx={{ display: 'flex', gap: 0.25, mr: 0.5 }}>
                {' '}
                {/* Reduced gap and margin */}
                {starredProducts.slice(0, 3).map((product, index) => (
                  <Box
                    key={product.cref}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 0.5,
                      overflow: 'hidden',
                      border: `1px solid ${muiTheme.palette.divider}`,
                      backgroundColor: muiTheme.palette.background.default,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                    }}
                  >
                    {product.img_url ? (
                      <img
                        src={product.img_url}
                        alt={product.display}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      product.display.charAt(0).toUpperCase()
                    )}
                  </Box>
                ))}
              </Box>
              {showProducts ? (
                <ExpandLessIcon fontSize="small" sx={{ color: colors.fg }} />
              ) : (
                <ExpandMoreIcon fontSize="small" sx={{ color: colors.fg }} />
              )}
            </Box>
          )}
        </Box>{' '}
        {/* End of top bar section */}
        {/* Expanded ToC Content */}
        <Collapse in={showToc}>
          <Box
            component="nav"
            role="navigation"
            aria-label="Table of contents navigation"
            sx={{
              borderTop: `1px solid ${muiTheme.palette.divider}`,
              p: 1, // Reduced from 2
            }}
          >
            {topics.map((topic, index) => (
              <Box
                key={index}
                onClick={() => handleTopicSelect(index)}
                role="button"
                tabIndex={0}
                aria-label={`Go to ${topic.summary}`}
                aria-current={index === currentTopicIndex ? 'location' : undefined}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleTopicSelect(index);
                  }
                }}
                sx={{
                  px: 1, // Reduced from 1.5
                  py: 0.75, // Reduced from 1.5
                  mb: 0.25, // Reduced from 0.5
                  borderRadius: 1,
                  cursor: 'pointer',
                  backgroundColor:
                    index === currentTopicIndex ? muiTheme.palette.action.selected : 'transparent',
                  '&:hover': {
                    backgroundColor: muiTheme.palette.action.hover,
                  },
                  transition: 'background-color 0.2s ease',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: index === currentTopicIndex ? muiTheme.palette.primary.main : colors.fg,
                    fontWeight: index === currentTopicIndex ? 500 : 400,
                  }}
                >
                  {topic.summary}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
        {/* Expanded Products Content */}
        <Collapse in={showProducts}>
          <Box
            sx={{
              borderTop: `1px solid ${muiTheme.palette.divider}`,
              p: 1, // Reduced from 2
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                pb: 1,
                '&::-webkit-scrollbar': {
                  height: 4,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: muiTheme.palette.divider,
                  borderRadius: 2,
                },
              }}
            >
              {starredProducts.map((product) => (
                <Box
                  key={product.cref}
                  sx={{
                    flexShrink: 0,
                    width: '100%',
                    maxWidth: 200, // Set max width to 200px
                    minWidth: 160, // Set min width to 160px
                  }}
                >
                  <ProductMiniCard concept={product} variant="inline" />
                </Box>
              ))}
            </Box>
          </Box>
        </Collapse>
      </Box>{' '}
      {/* End of main container */}
    </Box>
  );
};
